/**
 * Turning a screen — or a file someone picked — into something small enough to send.
 *
 * Both paths end at the same place: a canvas, downscaled to a sane width and encoded as
 * JPEG. A raw capture of a dense table at 2× is several megabytes and mostly flat colour;
 * at 1600px and quality 0.72 the same image is well under a hundred kilobytes and still
 * legible enough to read a chip label, which is the only thing it is for.
 */

import { domToCanvas } from "modern-screenshot";

export interface Shot {
  dataUrl: string;
  width: number;
  height: number;
  origin: "capture" | "upload";
}

/** Wide enough to read a table row, small enough that six of them still fit in one request. */
const MAX_WIDTH = 1600;
const QUALITY = 0.72;

/** Draw a source onto a canvas no wider than MAX_WIDTH, and encode it. */
function encode(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  origin: Shot["origin"],
): Shot {
  const scale = Math.min(1, MAX_WIDTH / sourceWidth);
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("This browser would not give us a canvas to draw on");
  /* JPEG has no alpha; without a ground, transparent regions come out black. */
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);

  return { dataUrl: canvas.toDataURL("image/jpeg", QUALITY), width, height, origin };
}

/**
 * Photograph the page as it stands.
 *
 * `modern-screenshot` re-renders the DOM rather than photographing it, so this is a good
 * likeness and not a true one — cross-origin images and canvas content can come out blank.
 * For SchoolVerse, which is text and boxes over a local stylesheet, the likeness is close.
 *
 * The widget's own dialog is excluded, or every screenshot would be a picture of the form
 * being filled in.
 */
export async function captureScreen(): Promise<Shot> {
  const canvas = await domToCanvas(document.body, {
    /* Capture at 1× whatever the display does; we are about to downscale anyway. */
    scale: 1,
    backgroundColor: "#ffffff",
    filter: (node) =>
      !(node instanceof Element && node.closest?.("[data-report-widget]")),
  });
  return encode(canvas, canvas.width, canvas.height, "capture");
}

/** The same treatment for an image someone attached themselves. */
export async function shotFromFile(file: File): Promise<Shot> {
  if (!file.type.startsWith("image/")) throw new Error(`${file.name} is not an image`);
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`${file.name} could not be read as an image`));
      el.src = url;
    });
    return encode(img, img.naturalWidth, img.naturalHeight, "upload");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Roughly what a data URL will weigh on the wire, for the "too big" guard in the widget. */
export function bytesOf(shot: Shot): number {
  return Math.floor((shot.dataUrl.length * 3) / 4);
}
